<template>
  <div ref="container" class="move-history">
    <div class="moves-table">
      <!-- Control header -->
      <div
        v-if="canNavigate"
        class="moves-table__header card is-flex is-justify-content-space-between"
        style="padding: 5px"
      >
        <button
          class="button"
          title="First move: ⬆️ Up arrow key"
          @click="gotoMove(-3)"
          :disabled="moveIndex < 0"
        >
          <span class="icon is-small fas fa-fast-backward"></span>
        </button>
        <button
          class="button"
          title="Previous move: ⬅️ Left arrow key"
          @click="gotoMove(moveIndex - 1)"
          :disabled="moveIndex < 0"
        >
          <span class="icon is-small fas fa-step-backward"></span>
        </button>
        <button
          class="button is-info"
          :class="
            autoplayDelay === AUTO_PLAY_DELAY ? 'is-danger' : 'is-outlined'
          "
          @click="autoplay(AUTO_PLAY_DELAY)"
          title="Autoplay: 𓈙 Space key"
          :disabled="!fullMoveCount"
        >
          <span
            class="icon is-small fas"
            :class="autoplayDelay === AUTO_PLAY_DELAY ? 'fa-pause' : 'fa-play'"
          ></span>
        </button>
        <button
          class="button is-info"
          :class="
            autoplayDelay === FAST_PLAY_DELAY ? 'is-danger' : 'is-outlined'
          "
          @click="autoplay(FAST_PLAY_DELAY)"
          title="Fast autoplay: ↲ Enter key"
          :disabled="!fullMoveCount"
        >
          <span
            class="icon is-small fas"
            v-bind:class="
              autoplayDelay === FAST_PLAY_DELAY ? 'fa-pause' : 'fa-forward'
            "
          ></span>
        </button>
        <button
          class="button"
          title="Next move: ➡️ Right arrow key "
          @click="gotoMove(moveIndex + 1)"
          :disabled="!movesStats || moveIndex >= movesStats.length - 1"
        >
          <span class="icon is-small fas fa-step-forward"></span>
        </button>
        <button
          class="button"
          title="Last move: ⬇️ Down arrow key"
          @click="gotoMove(movesStats.length - 1)"
          :disabled="!movesStats || moveIndex >= movesStats.length - 1"
        >
          <span class="icon is-small fas fa-fast-forward"></span>
        </button>
      </div>

      <!-- Static header -->
      <table v-else style="width: 100%" class="moves-table__header">
        <b-loading :is-full-page="false" :active="isLoading"></b-loading>
        <thead>
          <tr>
            <th style="width: 20%" class="moves-table__header">Move</th>
            <th style="width: 40%" class="moves-table__header">White</th>
            <th style="width: 40%" class="moves-table__header">Black</th>
          </tr>
        </thead>
      </table>

      <!-- List of moves -->
      <table style="width: 100%">
        <b-loading :is-full-page="false" :active="isLoading"></b-loading>
        <tbody v-if="movesStats && match">
          <tr v-for="(n, i) in fullMoveCount" :key="i">
            <td style="width: 12%">{{ i + 1 }}</td>
            <td style="width: 44%">
              <MoveButton
                v-if="whiteMoves.length && whiteMoves[i]"
                :move="whiteMoves[i]"
                :isFocused="whiteMoves[i].moveIndex === moveIndex"
                :isPlaying="!match.result && playerColor"
                @onClick="gotoMove"
              />
            </td>
            <td>
              <MoveButton
                v-if="blackMoves.length && blackMoves[i]"
                :move="blackMoves[i]"
                :isFocused="blackMoves[i].moveIndex === moveIndex"
                :isPlaying="!match.result && playerColor"
                @onClick="gotoMove"
              />
            </td>
          </tr>
        </tbody>
      </table>

      <div class="moves-table__footer">
        <div v-if="match.result" class="moves-table__result">
          <p>
            <strong>{{ scoreText() }}</strong>
          </p>
          <em>{{ resultText() }}</em>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { CHESS_SIDE } from "vue-chess-shared/constants";
import MoveButton from "./MoveButton";
import GameHelper from "@/utils/game-helper";
import MoveHelper from "@/utils/move-helper";
import { GameDataBuilder } from "vue-chess-shared/GameDataBuilder";

const AUTO_PLAY_DELAY = 1000;
const FAST_PLAY_DELAY = 500;

export default {
  name: "MoveHistory",

  components: { MoveButton },

  props: {
    match: {
      type: Object,
      default: () => {},
    },
    movesStats: {
      type: Array,
      default: () => [],
    },
    blackToStart: {
      type: Number,
      default: 0,
    },
    playerColor: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      moveIndex: -1,
      replay: false,
      isAutoplaying: false,
      timerInterval: null,
      autoplayDelay: null,
      AUTO_PLAY_DELAY,
      FAST_PLAY_DELAY,
    };
  },

  computed: {
    isLoading() {
      return !this.movesStats;
    },

    canNavigate() {
      // can navigate only after match ended (has result) or its ai game
      return this.match && this.match.result;
    },

    fullMoveCount() {
      return MoveHelper.fullMoveCount(this.movesStats, this.blackToStart);
    },

    blackMoves() {
      return this.movesStats.filter((m) => m.color === CHESS_SIDE.BLACK);
    },

    whiteMoves() {
      return this.movesStats.filter((m) => m.color === CHESS_SIDE.WHITE);
    },
  },

  watch: {
    movesStats: {
      immediate: true,
      handler(movesStats) {
        if (!movesStats || !movesStats.length) {
          this.moveIndex = -3; // to avoid cases of movesStats.length is indeed 0
          this.gotoMove(-3);
          return;
        }

        if (this.replay && !this.isAutoplaying) {
          this.autoplay(1000);
          return;
        }

        // Check createdAt to avoid case of new move and firebase didn't return createdAt timestamp
        if (
          !this.isAutoplaying &&
          movesStats[movesStats.length - 1].createdAt
        ) {
          this.gotoMove(movesStats.length - 1);
        }

        if (this.moveIndex === movesStats.length - 1) {
          this.$nextTick(() => {
            this.$refs.container.scrollTo(0, this.$refs.container.scrollHeight);
          });
        }
      },
    },
  },

  mounted() {
    document.addEventListener("keydown", this.handleGlobalKeypress);
  },

  beforeDestroy() {
    document.removeEventListener("keydown", this.handleGlobalKeypress);
  },

  methods: {
    handleGlobalKeypress(event) {
      if (!this.canNavigate) return;

      const KEY_ACTIONS = {
        ArrowUp: () => this.gotoMove(-3),
        ArrowDown: () => this.gotoMove(this.movesStats.length - 1),
        ArrowLeft: () => this.gotoMove(this.moveIndex - 1),
        ArrowRight: () => this.gotoMove(this.moveIndex + 1),
        " ": () => this.autoplay(AUTO_PLAY_DELAY), // Space key
        Enter: () => this.autoplay(FAST_PLAY_DELAY),
      };

      const action = KEY_ACTIONS[event.key];
      if (action) {
        event.preventDefault();
        action();
      }
    },

    gotoMove(moveIndex) {
      if (!this.movesStats) return;

      if (moveIndex < this.movesStats.length) {
        const gameDataBuilder = new GameDataBuilder(
          this.match,
          this.movesStats
        );

        let timeData = null;

        if (this.match.result) {
          // time at specific move after match ended
          timeData = gameDataBuilder.timeData(moveIndex);
        } else {
          // time at latest move while playing
          timeData = gameDataBuilder.timeData();
        }

        let pgn = "";

        if (moveIndex >= 0) {
          const moveStats = this.movesStats[moveIndex];
          pgn = moveStats ? moveStats.pgn : "";
        }

        this.$emit("onViewMove", {
          moveIndex,
          pgn,
          timeData,
        });
      }

      if (moveIndex < this.moveIndex) {
        this.stopAutoplay();
      }

      this.moveIndex = Math.max(-1, moveIndex);

      if (moveIndex >= this.movesStats.length) {
        this.stopAutoplay();
        this.moveIndex = this.movesStats.length - 1;
      }
    },

    autoplay(delay) {
      if (!this.movesStats || !this.movesStats.length) return;
      if (this.isAutoplaying && this.autoplayDelay === delay) {
        return this.stopAutoplay();
      }
      clearInterval(this.timerInterval);
      this.isAutoplaying = true;
      this.autoplayDelay = delay;

      if (this.moveIndex >= this.movesStats.length - 1) {
        this.gotoMove(0);
      }

      this.timerInterval = setInterval(() => {
        this.gotoMove(this.moveIndex + 1);
      }, delay);
    },

    stopAutoplay() {
      clearInterval(this.timerInterval);
      this.isAutoplaying = false;
      this.timerInterval = null;
      this.autoplayDelay = null;
    },

    scoreText() {
      return GameHelper.scoreText(
        this.match.player_1_score,
        this.match.player_2_score
      );
    },

    resultText() {
      return GameHelper.resultText(
        this.match.player_1_score,
        this.match.player_2_score,
        this.match.result
      );
    },
  },

  created() {
    this.replay = !!this.$route.query.replay;
  },
};
</script>

<style scoped>
.move-history {
  height: 100%;
  border: 2px solid #b7dcff;
  background-color: aliceblue;
  align-items: center;
  overflow: scroll;
  overflow-x: hidden;
}

table {
  position: relative;
  border-collapse: collapse;
}

tbody {
  overflow: scroll;
}

th,
td {
  padding: 0.25rem;
  vertical-align: bottom;
}

.moves-table__header {
  position: sticky;
  top: 0; /* Don't forget this, required for the stickiness */
  z-index: 1;
  background-color: #b7dcff;
}

.moves-table__footer {
  padding-bottom: 13rem;
}

.moves-table__result {
  border: 1px solid lightgray;
}
</style>
